import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

export const AMBIENT_TIMELINE_INTERVAL_MS = 4500;
export const COMPARISON_DIFF_REVEAL_INTERVAL_MS = 360;
export const FAST_GLOBAL_BURST_REVEAL_INTERVAL_MS = 100;

const AMBIENT_MESSAGE_KEYS = Array.from(
    { length: 12 },
    (_, index) => `sidebar.fastGlobalTimeline.ambient.messages.${index}`
);
const MAX_VISIBLE_AMBIENT_MESSAGES = 5;

const shuffled = (items) => {
    const result = [...items];
    for (let index = result.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(Math.random() * (index + 1));
        [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
    }
    return result;
};

const createAmbientTimeline = () => {
    const order = shuffled(AMBIENT_MESSAGE_KEYS);
    return {
        order,
        nextIndex: 1,
        serial: 1,
        entries: [{ id: 0, key: order[0] }],
    };
};

const comparableText = (value = '') => String(value)
    .toLocaleLowerCase()
    .replace(/[\s….,;:!?]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();

export const hasDistinctObservationHeadline = (headline, message) => {
    const normalizedHeadline = comparableText(headline);
    const normalizedMessage = comparableText(message);
    if (!normalizedHeadline || !normalizedMessage) return false;
    return normalizedHeadline !== normalizedMessage
        && !normalizedMessage.startsWith(normalizedHeadline);
};

const observationCausalId = (observation) => String(
    observation?.causal_id || observation?.observation_id || ''
).trim();

const useBurstSmoothedObservations = (observations, { enabled, isLoading }) => {
    const [revealed, setRevealed] = useState([]);
    const pendingRef = useRef([]);
    const knownIdsRef = useRef(new Set());
    const timerRef = useRef(null);
    const mountedRef = useRef(true);

    const cancelTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const reset = useCallback(() => {
        cancelTimer();
        pendingRef.current = [];
        knownIdsRef.current = new Set();
        if (mountedRef.current) {
            setRevealed((current) => (current.length > 0 ? [] : current));
        }
    }, [cancelTimer]);

    const scheduleBacklog = useCallback(() => {
        if (timerRef.current !== null || pendingRef.current.length === 0) return;
        const revealNext = () => {
            timerRef.current = null;
            if (!mountedRef.current) return;
            const next = pendingRef.current.shift();
            if (next) {
                setRevealed((current) => (
                    current.some((item) => observationCausalId(item) === observationCausalId(next))
                        ? current
                        : [...current, next]
                ));
            }
            if (pendingRef.current.length > 0) {
                timerRef.current = setTimeout(
                    revealNext,
                    FAST_GLOBAL_BURST_REVEAL_INTERVAL_MS
                );
            }
        };
        timerRef.current = setTimeout(revealNext, FAST_GLOBAL_BURST_REVEAL_INTERVAL_MS);
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            cancelTimer();
            pendingRef.current = [];
            knownIdsRef.current = new Set();
        };
    }, [cancelTimer]);

    useLayoutEffect(() => {
        if (!enabled) {
            reset();
            return;
        }
        const uniqueIncoming = observations.filter((item, index, items) => {
            const causalId = observationCausalId(item);
            return causalId && items.findIndex(
                (candidate) => observationCausalId(candidate) === causalId
            ) === index;
        });
        if (uniqueIncoming.length === 0) {
            reset();
            return;
        }

        const incomingIds = new Set(uniqueIncoming.map(observationCausalId));
        const staleRunDetected = [...knownIdsRef.current].some((id) => !incomingIds.has(id));
        if (staleRunDetected) {
            cancelTimer();
            pendingRef.current = [];
            knownIdsRef.current = new Set();
            setRevealed([]);
        }

        if (!isLoading) {
            cancelTimer();
            pendingRef.current = [];
            knownIdsRef.current = incomingIds;
            setRevealed(uniqueIncoming);
            return;
        }

        const newItems = uniqueIncoming.filter(
            (item) => !knownIdsRef.current.has(observationCausalId(item))
        );
        newItems.forEach((item) => knownIdsRef.current.add(observationCausalId(item)));
        if (newItems.length === 0) return;

        if (pendingRef.current.length === 0 && timerRef.current === null) {
            const [immediate, ...backlog] = newItems;
            setRevealed((current) => (
                current.some((item) => observationCausalId(item) === observationCausalId(immediate))
                    ? current
                    : [...current, immediate]
            ));
            pendingRef.current.push(...backlog);
        } else {
            pendingRef.current.push(...newItems);
        }
        scheduleBacklog();
    }, [cancelTimer, enabled, isLoading, observations, reset, scheduleBacklog]);

    return enabled ? revealed : observations;
};

const FastGlobalObservations = ({
    observations = [],
    isLoading = false,
    analysisStarted = false,
    mode = 'fast_global',
    terminalStatus = null,
}) => {
    const { t } = useTranslation('review');
    const visible = useMemo(
        () => (Array.isArray(observations) ? observations : []),
        [observations]
    );
    const isComparison = mode === 'comparison';
    const burstSmoothed = useBurstSmoothedObservations(visible, {
        enabled: !isComparison,
        isLoading,
    });
    const streamScrollRef = useRef(null);
    const comparisonCandidateIdentityRef = useRef([]);
    const [revealedComparisonDiffCount, setRevealedComparisonDiffCount] = useState(0);
    const [ambientTimeline, setAmbientTimeline] = useState(createAmbientTimeline);
    const ambientWasVisibleRef = useRef(false);
    const comparisonCandidates = useMemo(() => visible
        .map((item, arrivalIndex) => ({ item, arrivalIndex }))
        .filter(({ item }) => String(item.observation_id || '').startsWith('comparison-candidate-'))
        // A preview replaces the detected observation and is deliberately moved by
        // the reducer. Keep the comparison timeline in the deterministic order
        // supplied by the backend instead of treating that replacement as a new run.
        .sort((left, right) => {
            const leftIndex = Number(left.item.candidate_index);
            const rightIndex = Number(right.item.candidate_index);
            const leftHasIndex = Number.isInteger(leftIndex) && leftIndex >= 0;
            const rightHasIndex = Number.isInteger(rightIndex) && rightIndex >= 0;
            if (leftHasIndex && rightHasIndex && leftIndex !== rightIndex) {
                return leftIndex - rightIndex;
            }
            if (leftHasIndex !== rightHasIndex) return leftHasIndex ? -1 : 1;
            return left.arrivalIndex - right.arrivalIndex;
        })
        .map(({ item }) => item), [visible]);
    const comparisonDiffVersion = comparisonCandidates
        .map((item) => item.observation_id)
        .join('|');
    const displayedComparisonDiffCount = Math.min(
        comparisonCandidates.length,
        Math.max(comparisonCandidates.length ? 1 : 0, revealedComparisonDiffCount)
    );
    const showComparisonEvaluationTail = isComparison
        && isLoading
        && comparisonCandidates.length > 0
        && displayedComparisonDiffCount >= comparisonCandidates.length;
    const displayed = isComparison
        ? [
            ...visible.filter((item) => !String(item.observation_id || '').startsWith('comparison-candidate-')),
            ...comparisonCandidates.slice(0, displayedComparisonDiffCount),
        ]
        : burstSmoothed;
    const issuesStarted = displayed.length > 0;
    const showAmbientTimeline = isLoading
        && !issuesStarted
        && (analysisStarted || isComparison);
    const displayedObservationVersion = displayed.map((item) => [
        item.observation_id,
        item.headline?.length || 0,
        item.message?.length || 0,
        item.causal_chain?.consequence?.length || 0,
    ].join(':')).join('|');
    useEffect(() => {
        if (showAmbientTimeline && !ambientWasVisibleRef.current) {
            setAmbientTimeline(createAmbientTimeline());
        }
        ambientWasVisibleRef.current = showAmbientTimeline;
        if (!showAmbientTimeline) return undefined;

        const timer = setInterval(() => {
            setAmbientTimeline((current) => {
                let order = current.order;
                let nextIndex = current.nextIndex;
                if (nextIndex >= order.length) {
                    order = shuffled(AMBIENT_MESSAGE_KEYS);
                    const previousKey = current.entries[current.entries.length - 1]?.key;
                    if (order[0] === previousKey && order.length > 1) {
                        [order[0], order[1]] = [order[1], order[0]];
                    }
                    nextIndex = 0;
                }
                const entry = { id: current.serial, key: order[nextIndex] };
                return {
                    order,
                    nextIndex: nextIndex + 1,
                    serial: current.serial + 1,
                    entries: [...current.entries, entry].slice(-MAX_VISIBLE_AMBIENT_MESSAGES),
                };
            });
        }, AMBIENT_TIMELINE_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [showAmbientTimeline]);

    useEffect(() => {
        if (!isComparison) return;
        const nextIds = comparisonCandidates.map((item) => item.observation_id);
        const previousIds = comparisonCandidateIdentityRef.current;
        comparisonCandidateIdentityRef.current = nextIds;
        setRevealedComparisonDiffCount((current) => {
            if (!nextIds.length) {
                return 0;
            }
            // The observations are cleared between analyses. Within an analysis,
            // previews may reorder existing candidates; that must never restart the
            // paced deterministic feed.
            if (!previousIds.length) return 1;
            return Math.max(1, Math.min(current, nextIds.length));
        });
    }, [comparisonCandidateIdentityRef, comparisonCandidates, comparisonDiffVersion, isComparison]);

    useEffect(() => {
        if (
            !isComparison
            || revealedComparisonDiffCount <= 0
            || revealedComparisonDiffCount >= comparisonCandidates.length
        ) return undefined;
        const timer = setTimeout(() => {
            setRevealedComparisonDiffCount((current) => Math.min(
                current + 1,
                comparisonCandidates.length
            ));
        }, COMPARISON_DIFF_REVEAL_INTERVAL_MS);
        return () => clearTimeout(timer);
    }, [comparisonCandidates.length, isComparison, revealedComparisonDiffCount]);

    useLayoutEffect(() => {
        const element = streamScrollRef.current;
        if (element) element.scrollTop = element.scrollHeight;
    }, [ambientTimeline.entries, displayedObservationVersion, showComparisonEvaluationTail]);

    const handleWheel = (event) => {
        const element = event.currentTarget;
        const scale = event.deltaMode === 1
            ? 16
            : event.deltaMode === 2
                ? element.clientHeight
                : 1;
        const delta = event.deltaY * scale;
        const maxScrollTop = Math.max(0, element.scrollHeight - element.clientHeight);
        const nextScrollTop = Math.max(0, Math.min(maxScrollTop, element.scrollTop + delta));

        if (nextScrollTop !== element.scrollTop) {
            event.preventDefault();
            element.scrollTop = nextScrollTop;
        }
    };

    const showPartialTerminal = terminalStatus === 'partial';
    if (!displayed.length && !showAmbientTimeline && !showPartialTerminal) return null;

    return (
        <section
            ref={streamScrollRef}
            className={`fast-global-observations ${isLoading ? 'is-thinking' : ''} ${isComparison ? 'is-comparison' : ''}`}
            aria-live="polite"
            aria-label={t('sidebar.fastGlobalTimeline.label')}
            onWheel={handleWheel}
        >
            {showPartialTerminal && (
                <div className="fast-global-partial-terminal" role="status">
                    <strong>{t('sidebar.fastGlobalTimeline.partialTerminalTitle')}</strong>
                    <span>{t('sidebar.fastGlobalTimeline.partialTerminalMessage')}</span>
                </div>
            )}
            {showAmbientTimeline && (
                <section
                    className="fast-global-ambient-timeline"
                    aria-label={t('sidebar.fastGlobalTimeline.ambient.label')}
                >
                    <header className="fast-global-ambient-timeline__header">
                        <span className="fast-global-ambient-timeline__mark" aria-hidden="true"><i /></span>
                        <span>
                            <small>{t('sidebar.fastGlobalTimeline.engineEyebrow')}</small>
                            <strong>{t('sidebar.fastGlobalTimeline.ambient.title')}</strong>
                        </span>
                        <span className="fast-global-ambient-timeline__live" aria-hidden="true">
                            <i /><i /><i />
                        </span>
                    </header>
                    <ol className="fast-global-ambient-timeline__list">
                        {ambientTimeline.entries.map((entry, index) => (
                            <li
                                className={index === ambientTimeline.entries.length - 1 ? 'is-active' : 'is-complete'}
                                key={entry.id}
                            >
                                <span className="fast-global-ambient-timeline__track" aria-hidden="true"><i /></span>
                                <p>{t(entry.key)}</p>
                            </li>
                        ))}
                    </ol>
                </section>
            )}

            {displayed.length > 0 && (
                <div className="fast-global-engine">
                    <header className="fast-global-engine__header">
                        <span className="fast-global-engine__signal" aria-hidden="true">
                            <i /><i /><i /><i />
                        </span>
                        <span className="fast-global-engine__identity">
                            <span>{t('sidebar.fastGlobalTimeline.engineEyebrow')}</span>
                            <strong>{t('sidebar.fastGlobalTimeline.engineTitle')}</strong>
                        </span>
                        <span className="fast-global-engine__metric">
                            <strong>{String(displayed.length).padStart(2, '0')}</strong>
                            <span>{t('sidebar.fastGlobalTimeline.engineCount')}</span>
                        </span>
                    </header>
                    <span className="fast-global-engine__pulse" aria-hidden="true"><i /></span>
                    <div className="fast-global-observation-feed">
                        {displayed.map((observation, index) => {
                            const stage = observation.stage || 'validation';
                            const previousStage = displayed[index - 1]?.stage || 'validation';
                            const showStage = index === 0 || stage !== previousStage;
                            const comparisonStatus = observation.comparison_state === 'assessment_preview'
                                ? t(`sidebar.fastGlobalTimeline.comparisonAssessments.${observation.assessment || 'uncertain'}`)
                                : null;
                            return (
                                <article
                                    className={`fast-global-observation ${index === displayed.length - 1 ? 'is-active' : 'is-complete'} ${isComparison && observation.comparison_state === 'detected' ? 'is-comparison-detected' : ''}`}
                                    key={observation.observation_id}
                                >
                                    <span className="fast-global-observation__track" aria-hidden="true">
                                        <span className="fast-global-observation__index">
                                            {String(index + 1).padStart(2, '0')}
                                        </span>
                                    </span>
                                    <div className="fast-global-observation__content">
                                        <div className="fast-global-observation__meta">
                                            {showStage && (
                                                <span className="fast-global-observation__stage">
                                                    {t(`sidebar.fastGlobalTimeline.stages.${stage}`)}
                                                </span>
                                            )}
                                            {observation.evidence_count > 0 && (
                                                <span className="fast-global-observation__evidence">
                                                    {t('sidebar.fastGlobalTimeline.evidenceCount', {
                                                        count: observation.evidence_count,
                                                    })}
                                                </span>
                                            )}
                                            {observation.provisional === true
                                                && !isComparison
                                                && !observation.comparison_state
                                                && (
                                                    <span className="fast-global-observation__provisional">
                                                        {t(isLoading
                                                            ? 'sidebar.fastGlobalTimeline.provisionalInProgress'
                                                            : 'sidebar.fastGlobalTimeline.provisionalRetained')}
                                                    </span>
                                            )}
                                        </div>
                                        {comparisonStatus && (
                                            <div className="fast-global-observation__assessment-row">
                                                <span className={`fast-global-observation__assessment is-${observation.assessment || 'pending'}`}>
                                                    {comparisonStatus}
                                                </span>
                                            </div>
                                        )}
                                        {hasDistinctObservationHeadline(observation.headline, observation.message) && (
                                            <p className="fast-global-observation__headline">
                                                {observation.headline}
                                            </p>
                                        )}
                                        <p>{observation.message}</p>
                                        {observation.causal_chain?.consequence
                                            && comparableText(observation.causal_chain.consequence) !== comparableText(observation.message)
                                            && (
                                                <p className="fast-global-observation__consequence">
                                                    {observation.causal_chain.consequence}
                                                </p>
                                            )}
                                    </div>
                                </article>
                            );
                        })}
                        {showComparisonEvaluationTail && (
                            <div className="fast-global-stream-tail comparison-evaluation-tail" role="status">
                                <span className="fast-global-stream-tail__track" aria-hidden="true"><span /></span>
                                <span className="fast-global-stream-tail__signal">
                                    <span className="fast-global-stream-tail__label">
                                        {t('sidebar.fastGlobalTimeline.comparisonProcessing')}
                                    </span>
                                    <span className="fast-global-stream-tail__dots" aria-hidden="true"><i /><i /><i /></span>
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};

export default FastGlobalObservations;
